import { z } from "zod";
import {
  localizedTextSchema,
  type LocalizedText,
} from "@modules/rounds/_shared";

/**
 * A document requirement as it exists ON THE SERVER.
 *
 * Unlike questions, these are not part of the wizard's client draft. Creating a round seeds three
 * of them (cv / pitch-deck / supporting-documents), so the panel LOADS them and edits them one row
 * at a time — inventing local rows would post duplicates of things that already exist.
 */

/** Bytes per megabyte. The API stores bytes; the panel edits MB. */
const BYTES_PER_MB = 1024 * 1024;

/** The row shape `GET /document-requirements/` returns. */
export const DocumentRequirementSchema = z.object({
  id: z.string(),
  code: z.string(),
  name_fr: z.string(),
  name_ar: z.string(),
  name_en: z.string(),
  description_fr: z.string(),
  description_ar: z.string(),
  description_en: z.string(),
  is_required: z.boolean(),
  /** Lowercase, dot-stripped, as stored. Converted to catalogue keys for the picker. */
  accepted_extensions: z.array(z.string()),
  accepted_mime_types: z.array(z.string()),
  max_size_bytes: z.number().int(),
  max_files: z.number().int(),
  display_order: z.number().int(),
  is_active: z.boolean(),
});

export type DocumentRequirement = z.infer<typeof DocumentRequirementSchema>;

/** `EXPLICITLY_NONE` is a deliberate "this round needs no documents", not merely an empty list. */
export const DocumentPolicySchema = z.object({
  mode: z.enum(["CONFIGURED", "EXPLICITLY_NONE"]),
  confirmed_none_at: z.string().nullable(),
});

export type DocumentPolicy = z.infer<typeof DocumentPolicySchema>;

/**
 * The whole panel: the policy, its rows, and the revision the next mutation must echo.
 *
 * `configuration_frozen` is DEFAULTED, not required. Only the GET sends it — `reorder` and
 * `confirm-none` return `serialize_documents()` plus the revision and nothing else. Requiring it
 * made those two throw a DataContractError *after* a successful 200, which surfaced as a save
 * error on a save that had actually worked. Reaching a mutation at all means the round was
 * editable, so `false` is the right assumption.
 */
export const DocumentPanelSchema = z.object({
  policy: DocumentPolicySchema,
  requirements: z.array(z.unknown()),
  config_revision: z.number().int(),
  configuration_frozen: z.boolean().default(false),
});

/**
 * What one row looks like while being edited: names as a localized unit, size in MB, extensions
 * as catalogue keys. The service converts to and from the wire shape.
 *
 * Validation messages are i18n keys, resolved by the field via `t()`.
 */
export const DocumentDraftSchema = z.object({
  name: localizedTextSchema("round-create.errors.document-name-required"),
  /**
   * Optional help text the applicant sees under the upload card. Not `localizedTextSchema` —
   * that one requires all three, and an optional field must be allowed to be blank everywhere.
   */
  description: z.object({
    fr: z.string(),
    ar: z.string(),
    en: z.string(),
  }),
  is_required: z.boolean(),
  accepted_extensions: z
    .array(z.string())
    .min(1, "round-create.errors.document-formats-required"),
  max_size_mb: z
    .number()
    .int("round-create.errors.document-size-integer")
    .min(1, "round-create.errors.document-size-min"),
  max_files: z
    .number()
    .int("round-create.errors.document-files-integer")
    .min(1, "round-create.errors.document-files-min"),
});

export type DocumentDraft = z.infer<typeof DocumentDraftSchema>;

/** Server row -> editable draft. */
export function toDocumentDraft(
  row: DocumentRequirement,
  toCatalogue: (extensions: readonly string[]) => string[]
): DocumentDraft {
  return {
    name: { fr: row.name_fr, ar: row.name_ar, en: row.name_en },
    description: {
      fr: row.description_fr,
      ar: row.description_ar,
      en: row.description_en,
    },
    is_required: row.is_required,
    accepted_extensions: toCatalogue(row.accepted_extensions),
    // Rounded up so a non-round byte count never displays as 0 MB and then saves as 0.
    max_size_mb: Math.max(1, Math.ceil(row.max_size_bytes / BYTES_PER_MB)),
    max_files: row.max_files,
  };
}

/** MB -> bytes, at the one place the conversion happens. */
export function toMaxSizeBytes(megabytes: number): number {
  return megabytes * BYTES_PER_MB;
}

/**
 * A blank row. `is_required` starts TRUE even though the API defaults it to false: readiness
 * needs at least one REQUIRED active document, so an all-optional list cannot be scheduled —
 * defaulting to required is the choice that leads somewhere valid.
 */
export function emptyDocumentDraft(name: LocalizedText): DocumentDraft {
  return {
    name,
    description: { fr: "", ar: "", en: "" },
    is_required: true,
    accepted_extensions: ["PDF"],
    max_size_mb: 10,
    max_files: 1,
  };
}

/**
 * Predict the `code` the server will assign to a document requirement.
 *
 * ⚠ MUST MATCH `django.utils.text.slugify` EXACTLY, because `RoundDocumentRequirementListView`
 * does `values.setdefault("code", slugify(values["name_en"] or values["name_fr"]))` — the client
 * never sends a code, so the only way to reconcile a local draft against a row the server already
 * seeded is to compute the same slug it would.
 *
 * Django's order, reproduced here: NFKD-normalize, drop everything non-ASCII, lowercase, delete any
 * character that is not a word character / whitespace / hyphen, then collapse runs of whitespace and
 * hyphens into single hyphens and trim them from the ends.
 *
 * The consequential detail is that an apostrophe is DELETED, not replaced — so
 * « Pièce d'identité » becomes `piece-didentite`, not `piece-d-identite`. Verified against the
 * running backend rather than assumed.
 */
export function slugifyDocumentCode(value: string): string {
  return (
    value
      .normalize("NFKD")
      // Drop combining marks and anything else outside ASCII, as Django's
      // `.encode("ascii", "ignore")` does. A `\p{ASCII}` property escape rather than a
      // codepoint range: the range form contains control characters and trips ESLint's
      // `no-control-regex`.
      .replace(/[^\p{ASCII}]/gu, "")
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/[-\s]+/g, "-")
      .replace(/^[-_]+|[-_]+$/g, "")
  );
}

/**
 * The two documents a new round starts with, matching the design's « Documents requis » list.
 *
 * ⚠ `CV` IS DELIBERATELY CODE-COMPATIBLE with the backend's own seed. `DefaultRoundDocumentRequirement`
 * seeds `cv`, `pitch-deck` and `supporting-documents` when a round is created, and « CV » slugifies to
 * `cv` — so the sync updates that row rather than creating a second one beside it. « Proof of
 * identity » slugifies to `proof-of-identity`, which the backend does not seed, so it is created.
 * The two seeded rows nobody asked for are removed. All three branches are real.
 *
 * Names are given per locale rather than mirrored, because these are OUR defaults rather than
 * something an administrator typed — and the Arabic here is the same wording the backend seed uses.
 */
export function defaultDocumentDrafts(): DocumentDraft[] {
  return [
    {
      name: { fr: "CV", ar: "السيرة الذاتية", en: "CV" },
      description: { fr: "", ar: "", en: "" },
      is_required: true,
      // PDF plus images, because US-E3 is explicit that a photographed paper CV is a valid CV.
      accepted_extensions: ["PDF", "DOCX", "JPG", "PNG"],
      max_size_mb: 10,
      max_files: 1,
    },
    {
      name: {
        fr: "Pièce d'identité",
        ar: "وثيقة الهوية",
        en: "Proof of identity",
      },
      description: { fr: "", ar: "", en: "" },
      is_required: true,
      accepted_extensions: ["PDF", "JPG", "PNG"],
      max_size_mb: 10,
      max_files: 1,
    },
  ];
}
